import React, { useState } from "react";
import PropTypes from "prop-types";
import styles from "./Checkbox.module.css";

/**
 * Checkbox
 * @type React.FC<CheckboxPropTypes>
 */
const Checkbox = React.forwardRef((props, ref) => {
  Object.keys(props).forEach((p) => {
    if (!Object.keys(CheckboxPropTypes).includes(p)) {
      console.error(`Checkbox: 정의되지 않은 prop ${p}가 전달되었습니다.`);
    }
  });

  /** 외부에서 접근할 수 있는 함수들 */
  React.useImperativeHandle(ref, () => ({
    /** 값 삭제(초기화) */
    clear() {
      setCurrState((prev) => ({
        ...prev,
        checked: props.defaultChecked ?? false,
      }));
    },
    /** 값 가져오기 */
    getValue() {
      const { checked, value } = currState;
      if (checked) {
        return value;
      } else {
        return "";
      }
    },
    /** 값 지정 */
    setValue(v) {
      setCurrState((prev) => ({
        ...prev,
        value: v,
      }));
    },
    /** checked 여부 가져오기 */
    getChecked() {
      const { checked } = currState;
      return checked;
    },
    /** checked 상태 변경 */
    setChecked(chk) {
      setCurrState((prev) => ({
        ...prev,
        checked: chk,
      }));
    },
    enable() {
      setCurrState((prev) => ({
        ...prev,
        disabled: false,
      }));
    },
    disable() {
      setCurrState((prev) => ({
        ...prev,
        disabled: true,
      }));
    },
    setReadOnly: (isReadOnly) => {
      setCurrState((prev) => ({
        ...prev,
        readOnly: isReadOnly,
      }));
    },
    updateState(stateName, newValue) {
      // currState에 있는 값만 업데이트 가능
      if (Object.keys(currState).includes(stateName)) {
        setCurrState((prev) => ({
          ...prev,
          [stateName]: newValue,
        }));
      } else {
        console.error(
          "Checkbox: State에 지정되지 않은 값을 업데이트 시도하였습니다. 잘못된 시도입니다."
        );
      }
    },
  }));

  /**
   * Checkbox Component의 State
   */
  const [currState, setCurrState] = useState({
    textLabel: props.textLabel,
    disabled: props.disabled,
    readOnly: props.readOnly,
    checked: props.defaultChecked,
    value: props.defaultValue,
  });

  /**
   * 변경 이벤트
   */
  const _onChange = (e) => {
    setCurrState((prev) => {
      const updatedState = { ...prev, checked: !currState.checked };

      if (props.onChange) props.onChange(e, updatedState);

      return updatedState;
    });
  };

  return (
    <label
      className={`${styles.label} ${props.className ?? ""}`}
      htmlFor={props.id ?? currState.textLabel}
    >
      <input
        className={styles.checkbox}
        type={"checkbox"}
        id={props.id ?? currState.textLabel}
        name={currState.textLabel}
        onChange={_onChange}
        checked={currState.checked}
        value={currState.value}
        disabled={currState.disabled}
        readOnly={currState.readOnly}
      />
      <p className={styles.labelText}>{currState.textLabel}</p>
    </label>
  );
});

const CheckboxPropTypes = {
  /**
   * 텍스트. 비어있는 경우 width를 차지 하지 않음
   * updateState('textLabel', string) 함수로 수정 가능
   */
  textLabel: PropTypes.string,
  /**
   * disabled: 비활성화
   * 기본값 false
   * updateState('disabled', bool) 함수로 수정 가능
   */
  disabled: PropTypes.bool,
  /**
   * readOnly: 읽기전용
   * 기본값 false
   * updateState('readOnly', bool) 함수로 수정 가능
   */
  readOnly: PropTypes.bool,
  /**
   * 최초 check 값
   * setChecked(bool)로 변경
   * getChecked()로 데이터 참조
   */
  defaultChecked: PropTypes.bool,
  /**
   * 최초 value 값 (checked와 별개)
   * setValue(string)로 변경
   * getValue()로 데이터 참조
   */
  defaultValue: PropTypes.string,

  /**
   * 변경 이벤트
   */
  onChange: PropTypes.func,
  id: PropTypes.string,
};

Checkbox.propTypes = CheckboxPropTypes;
Checkbox.defaultProps = {
  textLabel: "checkbox",
  disabled: false,
  readOnly: false,
  defaultChecked: false,
  defaultValue: "on",
  onChange: null,
  id: "checkbox",
};
Checkbox.displayName = "Checkbox";
export default Checkbox;
